import { wallOpeningPlanExtents } from "building-common/openings/openingQuery";
import type { HeaderAssemblyEntity, HeaderAssemblyId } from "building-types/framing/headerAssemblyTypes";
import type { HeaderFramingSettings } from "building-types/settings/headerFramingSettings";
import type { WallFramingOpeningSettings } from "building-types/settings/wallFramingOpeningSettings";
import type { SheetGeometry } from "building-types/views/sheetGeometryTypes";
import { SheetGeometryType } from "building-types/views/sheetGeometryTypes";
import type { Table } from "common/data/table";
import { doorPlacementsOnLevel } from "doc/building/doorsOnLevel";
import { windowPlacementsOnLevel } from "doc/building/windowPlacementsOnLevel";
import getMergedDoorSettings from "doc/elements/doors/getMergedDoorSettings";
import { getMergedWindowSettings } from "doc/elements/window/getMergedWindowSettings";
import type { PlanCollider } from "doc/plans/interiorLabelObstacle";
import { CollisionType } from "doc/plans/interiorLabelObstacle";
import type { PlanGenerationContext } from "doc/plans/planGeometrySource";
import type { TaggedPlanGeometry } from "doc/plans/planGeometrySourceTypes";
import { makePlanColliderFromSheetGeometry } from "doc/plans/sheetGeometryCollision";
import { polyLine2EdgeLength } from "geom/polyLine2";
import type { Label } from "lib/house/plans/label";
import { labelNew, tagFontSize } from "lib/house/plans/label";
import { makeSheetGeometryFromLabels } from "./planLabels";
import { placeOpeningTags } from "./planTags";

export function resolveHeaderAssemblyId(
  overrideId: HeaderAssemblyId | undefined,
  gapLength: number,
  headerFramingSettings: HeaderFramingSettings,
): HeaderAssemblyId | undefined {
  if (overrideId) {
    return overrideId;
  }
  for (const rule of headerFramingSettings.rules) {
    if (gapLength <= rule.maxSpan) {
      return rule.headerAssemblyId;
    }
  }
  return undefined;
}

/**
 * Resolves the header assembly for plan tags. Explicit "None"
 * (`hasHeaderAssembly: false`) must not fall through to span-rule lookup.
 */
export function resolveOpeningHeaderAssemblyIdForPlanTag(
  framing: Partial<WallFramingOpeningSettings> | undefined,
  gapLength: number,
  headerFramingSettings: HeaderFramingSettings,
): HeaderAssemblyId | undefined {
  if (framing?.hasHeaderAssembly === false) {
    return undefined;
  }
  return resolveHeaderAssemblyId(framing?.headerAssemblyId, gapLength, headerFramingSettings);
}

export function resolveHeaderTagText(
  assemblyId: HeaderAssemblyId | undefined,
  headerAssemblies: Table<HeaderAssemblyEntity>,
): string | undefined {
  if (!assemblyId) {
    return undefined;
  }
  const assembly = headerAssemblies[assemblyId];
  if (!assembly) {
    return undefined;
  }
  return assembly.headerTag ?? assembly.name;
}

export function makeSheetGeometryFromHeaderFramingTagLabels({ building, levelId, noSettings }: PlanGenerationContext) {
  const { planset, headerFraming } = building.settings;
  const { paperSize } = planset;
  const fontSize = tagFontSize(paperSize);
  const headerAssemblies = building.doc.headerAssemblies;

  const tags: SheetGeometry[] = [];

  const windows = windowPlacementsOnLevel(levelId, building.windowPlacements);
  for (const window of windows) {
    if (!window.opening) {
      continue;
    }
    const merged = getMergedWindowSettings({
      window,
      descriptor: window.descriptor,
      homeSettings: building.settings,
      entitySettings: building.doc.entitySettings,
      activeSettingsSets: building.settingsSets,
      optionChoices: building.optionChoices,
      settings: building.doc.settings,
    });
    // Match the span used by makeRoughOpening (gap.end - gap.start + 2 * roughOpeningBorder).
    // Tagging against the raw opening width would mis-classify openings sitting exactly on a
    // rule boundary (e.g. a 48" window with default border = 1" actually frames at 50").
    const roughOpeningBorder = merged.framing?.roughOpeningBorder ?? 1;
    const gapLength = polyLine2EdgeLength(wallOpeningPlanExtents(window.opening), 0) + 2 * roughOpeningBorder;
    const assemblyId = resolveOpeningHeaderAssemblyIdForPlanTag(merged.framing, gapLength, headerFraming);
    const tagText = resolveHeaderTagText(assemblyId, headerAssemblies);
    if (!tagText) {
      continue;
    }

    const labels: Label[] = [];
    placeOpeningTags(
      window.opening,
      tagText,
      undefined,
      planset,
      window,
      (text, centerX, centerY, nX, nY, textRotationRads) => {
        labels.push(
          labelNew({
            text,
            fontSize,
            centerX,
            centerY,
            textRotationRads,
            relaxationNormalX: nX,
            relaxationNormalY: nY,
          }),
        );
      },
      { oppositeWallSide: true },
    );

    tags.push(
      ...makeSheetGeometryFromLabels({
        labels,
        handle: window.handle ?? building.levels[levelId].handle,
        noSettings,
      }),
    );
  }

  const doors = doorPlacementsOnLevel(levelId, building.doorPlacements);
  for (const door of doors) {
    if (!door.opening) {
      continue;
    }
    const merged = getMergedDoorSettings({
      door,
      descriptor: door.descriptor,
      homeSettings: building.settings,
      entitySettings: building.doc.entitySettings,
      activeSettingsSets: building.settingsSets,
      optionChoices: building.optionChoices,
      settings: building.doc.settings,
    });
    const roughOpeningBorder = merged.framing?.roughOpeningBorder ?? 1;
    const gapLength = polyLine2EdgeLength(wallOpeningPlanExtents(door.opening), 0) + 2 * roughOpeningBorder;
    const assemblyId = resolveOpeningHeaderAssemblyIdForPlanTag(merged.framing, gapLength, headerFraming);
    const tagText = resolveHeaderTagText(assemblyId, headerAssemblies);
    if (!tagText) {
      continue;
    }

    const labels: Label[] = [];
    placeOpeningTags(
      door.opening,
      tagText,
      undefined,
      planset,
      door,
      (text, centerX, centerY, nX, nY, textRotationRads) => {
        labels.push(
          labelNew({
            text,
            fontSize,
            centerX,
            centerY,
            textRotationRads,
            relaxationNormalX: nX,
            relaxationNormalY: nY,
          }),
        );
      },
      { oppositeWallSide: true },
    );

    tags.push(
      ...makeSheetGeometryFromLabels({
        labels,
        handle: door.handle ?? building.levels[levelId].handle,
        noSettings,
      }),
    );
  }

  return tags;
}

export function headerFramingTagPlanColliders(geometry: readonly TaggedPlanGeometry[]): PlanCollider[] {
  return geometry.reduce((acc, g) => {
    if (g.type === SheetGeometryType.TEXT) {
      acc.push(
        makePlanColliderFromSheetGeometry({
          collisionType: CollisionType.OpeningTag,
          sheetTextGeomSource: g,
        }),
      );
    }
    return acc;
  }, [] as PlanCollider[]);
}
